
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from "@/hooks/useAuth";
import ClassDetail from '@/components/classes/ClassDetail';
import { useDataServices } from '@/hooks/useDataServices';
import { Class } from '@/types/classTypes';
import { toast } from 'sonner';

const ClassDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { class: classService } = useDataServices();
  const [classData, setClassData] = useState<Class | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Get organization ID from user metadata
  const organizationId = user?.user_metadata?.organization_id || '';

  useEffect(() => {
    const fetchClassData = async () => {
      if (!id) return;
      
      // If id is "new", we're creating a new class, not fetching an existing one
      if (id === 'new') {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const data = await classService.getClassById(id);
        setClassData(data);
      } catch (err) {
        console.error("Error fetching class details:", err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        toast.error("Failed to load class details");
      } finally {
        setLoading(false);
      }
    };

    if (id && organizationId) {
      fetchClassData();
    } else {
      setLoading(false);
    }
  }, [id, organizationId, classService]);

  if (!id) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold mb-2">Class not found</h1>
          <p className="text-muted-foreground">The class ID is missing in the URL.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {!organizationId ? (
        <div className="p-4 text-center">
          <p className="text-red-500">No organization found. Please log in with a valid account.</p>
        </div>
      ) : (
        <ClassDetail 
          classId={id} 
          organizationId={organizationId} 
          initialData={classData}
          loading={loading}
          error={error}
        />
      )}
    </div>
  );
};

export default ClassDetailPage;
